import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { apiResponse } from 'src/app/models/apiResponse.model';
import { OrderByConfig, ApiFilter, FieldsSelection, DefaultFilter } from 'src/app/models/apiParams.model';
import { account, addAccountCompanySetting, addAccountEmployeeSetting, addAccountPersonSetting, changeAccountPassword, resetAccountPassword, updateAccountSetting, updateCompanySetting, updateNotification } from 'src/app/models/account.model';
import { updatePerson, personalLogin, companyLogin, employee } from 'src/app/models/crm.model';
import { deleteEmployeeAndTransfer, saveSettingGroupTypeSettings } from 'src/app/models/request.model';
import { accountingJob } from 'src/app/models/accounting.model';
import { BaseApiService } from '../base/base-api.service';

/**
 * Account Service - Handles account settings, passwords, employees, and accounting jobs
 */
@Injectable({ providedIn: 'root' })
export class AccountService extends BaseApiService {
    private readonly basePath = '/account/v1/';
    private readonly personPath = '/crm/v1/person/';
    private readonly employeePath = '/crm/v1/employee/';
    private readonly accountingPath = '/accounting/v1/job';
    private readonly notificationPath = '/account/v1/notification/';

    constructor(http: HttpClient) {
        super(http);
    }

    /*-------------------------
    == GENERAL CALLS ==
    -------------------------*/

    dateOperation(operation: string, days: number, date?: string): Observable<apiResponse> {
        return this.getAll<apiResponse>('/api/v1/date', {
            customParams: { operation, days, ...(date && { date }) }
        });
    }

    getApiJobs(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>('/api/v1/job/', { limit, page, orderBy, filter, fields, defaultFilter });
    }

    getApiAlerts(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>('/api/v1/alert/', { limit, page, orderBy, filter, fields, defaultFilter });
    }

    rerunApiJob(id: number, customRequestId?: string): Observable<apiResponse> {
        return this.putWithCustomId<apiResponse>(`/api/v1/job/${id}/rerun`, {}, customRequestId, 'json');
    }

    /*-------------------------
    == ACCOUNT SETTINGS CALLS ==
    -------------------------*/

    getAccountSettings(limit: number, page: number, orderBy: string | null, filter: string | ApiFilter | null, fields: FieldsSelection | null, defaultFilter: DefaultFilter | null, companyId?: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}setting/`, {
            limit, page, orderBy: orderBy || undefined, filter: filter || undefined, fields: fields || undefined, defaultFilter: defaultFilter || undefined,
            customParams: companyId !== undefined ? { companyId } : undefined
        });
    }

    getSettingTypes(limit: number, page: number, orderBy?: string | null, filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}setting-type`, { limit, page, orderBy: orderBy || undefined, filter, fields, defaultFilter });
    }

    getSettingGroupTypes(): Observable<apiResponse> {
        return this.options<apiResponse>(`${this.basePath}setting-group-type/`);
    }

    saveSettingGroupTypeSettings(id: number, data: saveSettingGroupTypeSettings, customRequestId?: string): Observable<apiResponse> {
        return this.postWithCustomId<apiResponse>(`${this.basePath}setting-group-type/${id}/settings`, data, customRequestId, 'json');
    }

    /*-------------------------
    == ACCOUNT PASSWORD CALLS ==
    -------------------------*/

    resetAccountPassword(data: resetAccountPassword): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}account/reset`, data, 'json');
    }

    changeAccountPassword(data: changeAccountPassword): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}account`, data, 'json');
    }

    /*-------------------------
    == ACCOUNT SETTINGS UPDATE ==
    -------------------------*/

    updateAccountModuleSetting(id: string, params: updateAccountSetting): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.basePath}module-setting/`, id, params, 'json');
    }

    updateAccountPersonSetting(id: string, params: updateAccountSetting): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.basePath}person-setting/`, id, params, 'json');
    }

    updateAccountEmployeeSetting(id: string, params: updateAccountSetting): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.basePath}employee-setting/`, id, params, 'json');
    }

    updateAccountCompanySetting(id: string, params: updateCompanySetting): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.basePath}company-setting/`, id, params, 'json');
    }

    updateAccountHoldingSetting(id: string, params: updateAccountSetting): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.basePath}holding-setting/`, id, params, 'json');
    }

    /*-------------------------
    == ACCOUNT SETTINGS ADD ==
    -------------------------*/

    addAccountPersonSetting(params: addAccountPersonSetting): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.basePath}person-setting`, params, 'json');
    }

    addAccountEmployeeSetting(params: addAccountEmployeeSetting): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.basePath}employee-setting`, params, 'json');
    }

    addAccountCompanySetting(params: addAccountCompanySetting): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.basePath}company-setting`, params, 'json');
    }

    /*-------------------------
    == ACCOUNT SETTINGS DELETE ==
    -------------------------*/

    deleteAccountModuleSetting(id: string): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.basePath}module-setting/`, id);
    }

    deleteAccountPersonSetting(id: string): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.basePath}person-setting/`, id);
    }

    deleteAccountEmployeeSetting(id: string): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.basePath}employee-setting/`, id);
    }

    deleteAccountCompanySetting(id: string): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.basePath}company-setting/`, id);
    }

    deleteAccountHoldingSetting(id: string): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.basePath}holding-setting/`, id);
    }

    /*-------------------------
    == ACCOUNT LOGIN CALLS ==
    -------------------------*/

    updateAccount(id: string, params: account): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.basePath}person-account/`, id, params, 'json');
    }

    updateCompanyAccount(id: string, params: companyLogin): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.basePath}company-account/`, id, params, 'json');
    }

    addPersonalLogin(data: personalLogin): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.basePath}person-account`, data, 'json');
    }

    addCompanyAccount(data: companyLogin): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.basePath}company-account`, data, 'json');
    }

    deleteCompanyAccount(id: string): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.basePath}company-account/`, id);
    }

    /*-------------------------
    == PERSON CALLS ==
    -------------------------*/

    getPersons(limit: number, page: number, orderBy?: string, filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(this.personPath, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    getPersonID(personID: number): Observable<apiResponse> {
        return this.getById<apiResponse>(this.personPath, personID);
    }

    getPersonEmail(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>('/crm/v1/person-email/', { limit, page, orderBy, filter, fields, defaultFilter });
    }

    getPersonPropertyId(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>('/crm/v1/person-property/', id, { fields });
    }

    addPerson(data: any): Observable<apiResponse> {
        return this.post<apiResponse>(this.personPath, data);
    }

    updatePerson(id: number, data: updatePerson): Observable<apiResponse> {
        return this.put<apiResponse>(this.personPath, id, data, 'json');
    }

    deletePersonEmail(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>('/crm/v1/person-email/', id);
    }

    deletePersonPhone(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>('/crm/v1/person-phone/', id);
    }

    /*-------------------------
    == EMPLOYEE CALLS ==
    -------------------------*/

    getEmployeeID(employeeID: number): Observable<apiResponse> {
        return this.getById<apiResponse>(this.employeePath, employeeID);
    }

    getEmployees(limit: number, page: number, orderBy?: string | null, filter?: string | ApiFilter | null, fields?: FieldsSelection | null, defaultFilter?: DefaultFilter | null): Observable<apiResponse> {
        return this.getAll<apiResponse>(this.employeePath, {
            limit, page, orderBy: orderBy || undefined, filter: filter || undefined, fields: fields || undefined, defaultFilter: defaultFilter || undefined
        });
    }

    getEmployeePropertyId(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>('/crm/v1/employee-property/', id, { fields });
    }

    sendCompanyLogin(employeeId: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.employeePath}${employeeId}/company-login`);
    }

    addEmployee(data: employee): Observable<apiResponse> {
        return this.post<apiResponse>(this.employeePath, data, 'json');
    }

    updateEmployee(id: number, data: employee): Observable<apiResponse> {
        return this.put<apiResponse>(this.employeePath, id, data, 'json');
    }

    deleteEmployee(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(this.employeePath, id);
    }

    deleteEmployeeProperty(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>('/crm/v1/employee-property/', id);
    }

    deleteEmployeeAndTransfer(id: number, data: deleteEmployeeAndTransfer): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.employeePath}${id}/remove`, data, 'json');
    }

    /*-------------------------
    == ACCOUNTING JOB CALLS ==
    -------------------------*/

    getAccountingJobs(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.accountingPath}`, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    runAccountingJobs(): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.accountingPath}/run`, {}, 'json');
    }

    deleteAccountingJob(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.accountingPath}/`, id);
    }

    addAccountingJob(data: accountingJob): Observable<apiResponse> {
        return this.post<apiResponse>(this.accountingPath, data, 'json');
    }

    /*-------------------------
    == NOTIFICATION CALLS ==
    -------------------------*/

    getNotifications(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(this.notificationPath, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    updateNotification(id: string | number, data: updateNotification): Observable<apiResponse> {
        return this.put<apiResponse>(this.notificationPath, id, data, 'json');
    }
}
